import React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";

import Header from "./components/Header";
import Hero from "./components/Hero";
import BookSpotlight from "./components/BookSpotlight";
import Verlag from "./components/Verlag";
import ContactForm from "./components/ContactForm";
import Footer from "./components/Footer";
import CookieBanner from "./components/CookieBanner";

import Impressum from "./pages/impressum";
import Privacy from "./pages/privacy";
import Terms from "./pages/terms";
import Cookies from "./pages/cookies";
import Services from "./pages/Services";
import ServiceDetail from "./pages/ServiceDetail";
import Books from "./pages/Books";
import BookDetail from "./pages/BookDetail";

import { SEGMENTS, swapLangPath } from "./lib/paths";

const LANGS = ["de", "es"];

const legalPages = { impressum: Impressum, privacy: Privacy, terms: Terms, cookies: Cookies };

function Home({ language }) {
  return (
    <>
      <Hero language={language} />
      <BookSpotlight language={language} />
      <Verlag language={language} />
      <ContactForm language={language} />
    </>
  );
}

/** Al cambiar de página, volver arriba; con ancla, dejar que el navegador salte. */
function ScrollToTop() {
  const { pathname, hash } = useLocation();
  React.useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.slice(1));
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
    }
    window.scrollTo(0, 0);
  }, [pathname, hash]);
  return null;
}

const SITIO = "https://www.bw-verlag.at";

function LangShell({ lang, children }) {
  const { pathname } = useLocation();

  // Cada página es canónica de sí misma, y declara su gemela en el otro
  // idioma. Antes todas apuntaban a la portada, y para Google eso las
  // convertía en copias de la portada.
  const limpia = (p) => (p.length > 1 ? p.replace(/\/+$/, "") : p);
  const aqui = limpia(pathname);
  const otro = lang === "de" ? "es" : "de";
  const gemela = limpia(swapLangPath(aqui, lang, otro));
  const enDe = lang === "de" ? aqui : gemela;
  const enEs = lang === "es" ? aqui : gemela;

  return (
    <div className="min-h-screen bg-tinta text-hueso flex flex-col">
      <Helmet>
        <html lang={lang} />
        <title>
          {lang === "de"
            ? "Blauer Wolf Verlag – Bücher aus Oberösterreich"
            : "Blauer Wolf Verlag – Libros desde Alta Austria"}
        </title>
        <meta
          name="description"
          content={
            lang === "de"
              ? "Unabhängiger Verlag aus Oberösterreich: Erzählliteratur auf Spanisch und Deutsch, im Haus lektoriert, gesetzt und gestaltet."
              : "Editorial independiente de Alta Austria: narrativa en español y alemán, corregida, maquetada y diseñada en casa."
          }
        />
        <meta property="og:site_name" content="Blauer Wolf Verlag" />
        <meta property="og:locale" content={lang === "de" ? "de_AT" : "es_ES"} />
        <meta property="og:url" content={`${SITIO}${aqui}`} />
        <meta property="og:image" content={`${SITIO}/sello-bwv.png`} />
        <meta name="twitter:card" content="summary_large_image" />
        <link rel="canonical" href={`${SITIO}${aqui}`} />
        <link rel="alternate" hrefLang="de" href={`${SITIO}${enDe}`} />
        <link rel="alternate" hrefLang="es" href={`${SITIO}${enEs}`} />
        <link rel="alternate" hrefLang="x-default" href={`${SITIO}${enDe}`} />
      </Helmet>

      <Header currentLanguage={lang} />
      <main className="flex-1">{children}</main>
      <Footer language={lang} />
      <CookieBanner language={lang} />
    </div>
  );
}

function PageWrapper({ lang, PageComponent }) {
  return (
    <LangShell lang={lang}>
      <PageComponent language={lang} />
    </LangShell>
  );
}

function RedirectByBrowserLang({ build }) {
  const params = useParams();
  const lang =
    typeof navigator !== "undefined" && navigator.language.startsWith("es") ? "es" : "de";
  return <Navigate to={build(lang, params)} replace />;
}

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<RedirectByBrowserLang build={(l) => `/${l}`} />} />

          {LANGS.map((lang) => (
            <React.Fragment key={lang}>
              <Route path={`/${lang}`} element={<PageWrapper lang={lang} PageComponent={Home} />} />

              {/* Catálogo */}
              <Route
                path={`/${lang}/${SEGMENTS.books[lang]}`}
                element={<PageWrapper lang={lang} PageComponent={Books} />}
              />
              <Route
                path={`/${lang}/${SEGMENTS.books[lang]}/:slug`}
                element={<PageWrapper lang={lang} PageComponent={BookDetail} />}
              />

              {/* Taller / Werkstatt */}
              <Route
                path={`/${lang}/${SEGMENTS.services[lang]}`}
                element={<PageWrapper lang={lang} PageComponent={Services} />}
              />
              <Route
                path={`/${lang}/${SEGMENTS.services[lang]}/:id`}
                element={<PageWrapper lang={lang} PageComponent={ServiceDetail} />}
              />

              {/* Legales */}
              {Object.entries(legalPages).map(([page, Component]) => (
                <Route
                  key={`${lang}-${page}`}
                  path={`/${lang}/${page}`}
                  element={<PageWrapper lang={lang} PageComponent={Component} />}
                />
              ))}
            </React.Fragment>
          ))}

          {/* Direcciones antiguas: que nadie caiga en una página en blanco */}
          <Route path="/de/libros/*" element={<Navigate to="/de/buecher" replace />} />
          <Route path="/es/buecher/*" element={<Navigate to="/es/libros" replace />} />
          <Route
            path="/de/services/:id"
            element={<RedirectByBrowserLang build={(l, p) => `/de/werkstatt/${p.id}`} />}
          />
          <Route
            path="/es/services/:id"
            element={<RedirectByBrowserLang build={(l, p) => `/es/taller/${p.id}`} />}
          />
          <Route path="/de/services" element={<Navigate to="/de/werkstatt" replace />} />
          <Route path="/es/services" element={<Navigate to="/es/taller" replace />} />
          <Route
            path="/services/:id"
            element={
              <RedirectByBrowserLang
                build={(l, p) => `/${l}/${SEGMENTS.services[l]}/${p.id}`}
              />
            }
          />
          {Object.keys(legalPages).map((page) => (
            <Route
              key={`bare-${page}`}
              path={`/${page}`}
              element={<RedirectByBrowserLang build={(l) => `/${l}/${page}`} />}
            />
          ))}

          <Route path="*" element={<RedirectByBrowserLang build={(l) => `/${l}`} />} />
        </Routes>
      </BrowserRouter>
    </HelmetProvider>
  );
}
